import { App as AntApp, Tooltip } from "antd";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { IconDownload, IconRefresh, IconSearch } from "../components/Icons";
import ScanProgress from "../components/ScanProgress";
import { Button, Empty, PageHeader, Pager, Pill, Segmented, Stat, Tag } from "../components/ui";
import { useWebSocket } from "../contexts/WebSocketContext";
import { errorMessage, post, resultsRows, safeGet } from "../lib/api";
import { MARKET_FLAG, downloadText, fmtDate, isNum, money, nextQuarterStart, num, pct, ratio, toCSV } from "../lib/format";
import { useTheme } from "../theme/ThemeContext";

const PAGE_SIZE = 25;
const MARKETS = ["US", "INDIA", "CHINA"];
const MARKET_TABS = [
  { value: "ALL", label: "All" },
  { value: "US", label: "US" },
  { value: "INDIA", label: "India" },
  { value: "CHINA", label: "China" },
];
const SORT_OPTIONS = [
  { label: "Rank score", value: "rank_score" },
  { label: "Quality", value: "quality_score" },
  { label: "Upside %", value: "upside_pct" },
  { label: "Price / Fair", value: "price_to_fair" },
  { label: "Loss chance", value: "loss_chance_pct" },
  { label: "ROE %", value: "roe_pct" },
  { label: "Revenue CAGR", value: "revenue_cagr_pct" },
  { label: "Timing score", value: "tech_total" },
];
const DIMS = [
  ["q_consistency", "Consistency"],
  ["q_growth", "Growth"],
  ["q_margin", "Margin"],
  ["q_roe", "ROE"],
  ["q_debt", "Debt"],
  ["q_wealth", "Wealth"],
  ["q_recent", "Recent"],
];
const CSV_COLUMNS = [
  "market", "rank", "ticker", "name", "sector", "action", "quality_score", "rank_score", "price", "currency",
  "fair_value", "price_to_fair", "upside_pct", "loss_chance_pct", "tech_total", "tech_labels", "quality_flags",
  "valuation_flags", "roe_pct", "revenue_cagr_pct", "op_margin_pct", "net_debt_to_equity",
];

const DEFAULT_FILTERS = {
  search: "",
  actions: ["BUY", "WATCH"],
  minQuality: 5,
  sector: "",
  sortBy: "rank_score",
  sortOrder: "desc",
};

function qualityColor(v) {
  if (!isNum(v)) return "var(--muted)";
  if (v >= 7) return "var(--good)";
  if (v >= 5) return "var(--warn)";
  return "var(--bad)";
}
function dimColor(v) {
  if (v >= 0.7) return "var(--good)";
  if (v >= 0.4) return "var(--gold)";
  return "var(--red)";
}
function pfColor(v) {
  if (!isNum(v)) return "var(--muted)";
  if (v <= 1) return "var(--good)";
  if (v <= 1.5) return "var(--warn)";
  return "var(--bad)";
}
function upsideColor(v) {
  if (!isNum(v)) return "var(--muted)";
  return v >= 0 ? "var(--good)" : "var(--bad)";
}
function lossColor(v) {
  if (!isNum(v)) return "var(--muted)";
  if (v <= 10) return "var(--good)";
  if (v <= 40) return "var(--warn)";
  return "var(--bad)";
}

function applyFilters(rows, f, market) {
  let out = rows;
  if (market !== "ALL") out = out.filter((r) => r.market === market);
  if (f.search) {
    const q = f.search.toLowerCase();
    out = out.filter((r) => (r.ticker || "").toLowerCase().includes(q) || (r.name || "").toLowerCase().includes(q));
  }
  if (f.actions.length) out = out.filter((r) => f.actions.includes(r.action));
  out = out.filter((r) => (r.quality_score ?? 0) >= f.minQuality);
  if (f.sector) out = out.filter((r) => r.sector === f.sector);
  const dir = f.sortOrder === "desc" ? -1 : 1;
  const miss = f.sortOrder === "desc" ? -Infinity : Infinity;
  return [...out].sort((a, b) => {
    const av = isNum(a[f.sortBy]) ? a[f.sortBy] : miss;
    const bv = isNum(b[f.sortBy]) ? b[f.sortBy] : miss;
    if (av === bv) return (a.rank ?? 0) - (b.rank ?? 0);
    return (av - bv) * dir;
  });
}

function QualityCell({ r }) {
  const tip = DIMS.map(([k, label]) => `${label}: ${isNum(r[k]) ? r[k].toFixed(2) : "—"}`).join("  ·  ");
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
      <span className="num" style={{ fontSize: 15, fontWeight: 600, color: qualityColor(r.quality_score) }}>
        {num(r.quality_score, 1)}
      </span>
      <Tooltip title={tip}>
        <div style={{ display: "flex", gap: 2, alignItems: "flex-end", height: 16 }} aria-label={tip} role="img">
          {DIMS.map(([k]) => {
            const v = isNum(r[k]) ? Math.max(0, Math.min(1, r[k])) : 0;
            return (
              <span
                key={k}
                style={{ width: 5, height: Math.max(2, Math.round(v * 16)), background: dimColor(v), borderRadius: 1 }}
              />
            );
          })}
        </div>
      </Tooltip>
    </div>
  );
}

function Row({ r, colorRows }) {
  const action = (r.action || "").toLowerCase();
  const cls = colorRows && ["buy", "watch", "avoid"].includes(action) ? `row-${action}` : "";
  const flags = [...(r.quality_flags || []), ...(r.valuation_flags || [])];
  return (
    <tr className={cls}>
      <td className="num" style={{ color: "var(--muted)" }}>{r.rank ?? "—"}</td>
      <td>
        <div style={{ display: "flex", flexDirection: "column" }}>
          <span style={{ fontWeight: 600 }}>
            {r.ticker}{" "}
            <span style={{ fontWeight: 400, color: "var(--muted)", fontSize: 11 }} title={r.market}>
              {MARKET_FLAG[r.market] || ""} {r.market}
            </span>
          </span>
          <span style={{ fontSize: 11, color: "var(--muted)" }}>
            {r.name}
            {r.sector ? ` · ${r.sector}` : ""}
          </span>
        </div>
      </td>
      <td>
        <Tooltip title={(r.reasons || []).join(" · ") || undefined}>
          <span>
            <Pill kind={action || "neutral"}>{r.action || "—"}</Pill>
          </span>
        </Tooltip>
      </td>
      <td>
        <QualityCell r={r} />
      </td>
      <td className="num" style={{ fontWeight: 500 }}>{num(r.rank_score, 1)}</td>
      <td className="num">{money(r.price, r.currency)}</td>
      <td>
        <div style={{ display: "flex", flexDirection: "column" }}>
          <span className="num">{money(r.fair_value, r.currency)}</span>
          <span className="num" style={{ fontSize: 11, color: pfColor(r.price_to_fair), fontWeight: 600 }}>
            {ratio(r.price_to_fair)}
          </span>
        </div>
      </td>
      <td className="num" style={{ color: upsideColor(r.upside_pct) }}>{pct(r.upside_pct)}</td>
      <td className="num" style={{ color: lossColor(r.loss_chance_pct) }}>{pct(r.loss_chance_pct, { sign: false })}</td>
      <td>
        <div className="tags" style={{ alignItems: "center" }}>
          <span className="num" style={{ fontSize: 11, color: "var(--info)", fontWeight: 600, marginRight: 2 }}>
            {r.tech_total ?? 0}/4
          </span>
          {(r.tech_labels || []).map((l) => (
            <Tag key={l} kind="info">{l}</Tag>
          ))}
        </div>
      </td>
      <td>
        <div className="tags">
          {flags.map((l) => (
            <Tag key={l} kind={l === "FXIgnored" ? "bad" : "neutral"}>{l}</Tag>
          ))}
        </div>
      </td>
    </tr>
  );
}

export default function Results() {
  const { message } = AntApp.useApp();
  const { colorRows } = useTheme();
  const { socket, enabled: wsEnabled } = useWebSocket();
  const outlet = useOutletContext() || {};
  const [rows, setRows] = useState([]);
  const [sectors, setSectors] = useState([]);
  const [scanDate, setScanDate] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [market, setMarket] = useState("ALL");
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [page, setPage] = useState(1);
  const [portfolioChanges, setPortfolioChanges] = useState(null);
  const [scan, setScan] = useState({ active: false, scanned: 0, total: 0, sector: null, elapsed: 0, message: null });
  const [starting, setStarting] = useState(false);
  const timerRef = useRef(null);
  const pollRef = useRef(null);

  const loadResults = useCallback(async () => {
    const data = await safeGet("/api/results", null);
    const list = resultsRows(data);
    setRows(list);
    setScanDate(data?.scan_date || null);
    const secs = await safeGet("/api/sectors", []);
    const fromRows = Array.from(new Set(list.map((r) => r.sector).filter(Boolean))).sort();
    setSectors(Array.isArray(secs) && secs.length ? secs : fromRows);
    setLoaded(true);
  }, []);

  useEffect(() => {
    loadResults();
    (async () => {
      const plans = await Promise.all(MARKETS.map((m) => safeGet(`/api/portfolio/${m}`, null)));
      let changes = 0;
      let any = false;
      plans.forEach((p) => {
        if (p?.last_plan) {
          any = true;
          changes += (p.last_plan.buy?.length || 0) + (p.last_plan.sell?.length || 0);
        }
      });
      setPortfolioChanges(any ? changes : null);
    })();
  }, [loadResults]);

  // ---- live progress: socket.io when available, else polling /api/status ----
  const startTimer = useCallback(() => {
    clearInterval(timerRef.current);
    timerRef.current = setInterval(() => setScan((s) => ({ ...s, elapsed: s.elapsed + 1 })), 1000);
  }, []);
  const stopTimer = useCallback(() => {
    clearInterval(timerRef.current);
    timerRef.current = null;
  }, []);

  useEffect(() => {
    if (!socket) return undefined;
    socket.on("scan_started", ({ total_stocks } = {}) => {
      setScan({ active: true, scanned: 0, total: total_stocks || 0, sector: null, elapsed: 0, message: null });
      startTimer();
    });
    socket.on("stock_scanned", ({ sector, scanned } = {}) => {
      setScan((s) => ({ ...s, active: true, scanned: scanned ?? s.scanned, sector: sector ?? s.sector }));
    });
    socket.on("stock_hit", (stock) => {
      if (!stock?.ticker) return;
      setRows((prev) => {
        const i = prev.findIndex((r) => r.ticker === stock.ticker);
        if (i >= 0) {
          const next = prev.slice();
          next[i] = stock;
          return next;
        }
        return [...prev, stock];
      });
    });
    socket.on("scan_complete", () => {
      stopTimer();
      setScan((s) => ({ ...s, active: false }));
      loadResults();
    });
    return () => {
      ["scan_started", "stock_scanned", "stock_hit", "scan_complete"].forEach((e) => socket.off(e));
      stopTimer();
    };
  }, [socket, loadResults, startTimer, stopTimer]);

  const pollStatus = useCallback(async () => {
    const st = await safeGet("/api/status", null);
    if (!st) return;
    if (st.state === "scanning") {
      setScan((s) => {
        const started = st.started_at ? Math.max(0, Math.round((Date.now() - new Date(st.started_at).getTime()) / 1000)) : s.elapsed;
        return { active: true, scanned: st.scanned || 0, total: st.total || 0, sector: st.sector, elapsed: started, message: st.message || null };
      });
    } else {
      setScan((s) => {
        if (s.active) loadResults();
        return { ...s, active: false };
      });
    }
  }, [loadResults]);

  useEffect(() => {
    if (wsEnabled && socket) return undefined;
    // Poll while scanning (30s), and once on mount to pick up an in-flight scan.
    pollStatus();
    pollRef.current = setInterval(() => {
      setScan((s) => {
        if (s.active) pollStatus();
        return s;
      });
    }, 30000);
    return () => clearInterval(pollRef.current);
  }, [wsEnabled, socket, pollStatus]);

  // sync with the shell's status when scanning started elsewhere
  const shellState = outlet.status?.state;
  useEffect(() => {
    if (shellState === "scanning") pollStatus();
  }, [shellState, pollStatus]);

  const runScan = async () => {
    setStarting(true);
    try {
      const res = await post("/api/scan", { market: market === "ALL" ? "ALL" : market });
      message.success(res?.message || (res?.mode === "cloud" ? "Scan dispatched to GitHub Actions" : "Scan started"));
      setScan({ active: true, scanned: 0, total: 0, sector: null, elapsed: 0, message: res?.mode === "cloud" ? "Running on GitHub Actions" : null });
      if (!(wsEnabled && socket)) {
        startTimer();
        setTimeout(pollStatus, 3000);
      }
    } catch (e) {
      message.error(errorMessage(e, "Could not start scan"));
    } finally {
      setStarting(false);
    }
  };

  // ---- derived ----
  const filtered = useMemo(() => applyFilters(rows, filters, market), [rows, filters, market]);
  useEffect(() => setPage(1), [filters, market]);
  const pages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const cur = Math.min(page, pages);
  const pageRows = filtered.slice((cur - 1) * PAGE_SIZE, cur * PAGE_SIZE);

  const stats = useMemo(() => {
    const scope = market === "ALL" ? rows : rows.filter((r) => r.market === market);
    const buys = scope.filter((r) => r.action === "BUY");
    const perMarket = MARKETS.map((m) => `${m === "INDIA" ? "India" : m === "CHINA" ? "China" : "US"} ${rows.filter((r) => r.market === m && r.action === "BUY").length}`).join(" · ");
    const withFair = scope.filter((r) => isNum(r.price_to_fair));
    const below = withFair.filter((r) => r.price_to_fair <= 1).length;
    const scored = scope.filter((r) => isNum(r.quality_score));
    const avgQ = scored.length ? scored.reduce((s, r) => s + r.quality_score, 0) / scored.length : null;
    const q5 = scored.filter((r) => r.quality_score >= 5).length;
    const flagCounts = {};
    scope.forEach((r) => {
      [...(r.quality_flags || []), ...(r.valuation_flags || [])].forEach((f) => {
        if (/thin|fx|nofair|stale|missing|approx/i.test(f)) flagCounts[f] = (flagCounts[f] || 0) + 1;
      });
    });
    const flagTotal = Object.values(flagCounts).reduce((a, b) => a + b, 0);
    const flagSub = Object.entries(flagCounts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 3)
      .map(([k, v]) => `${k} ${v}`)
      .join(" · ");
    return { buys: buys.length, perMarket, below, withFair: withFair.length, avgQ, q5, flagTotal, flagSub, total: scope.length };
  }, [rows, market]);

  const nq = nextQuarterStart(new Date());
  const nextRebalance = `next rebalance Q${nq.quarter} · ${nq.date.toLocaleDateString("en-GB", { day: "numeric", month: "short" })}`;

  const exportCsv = () => {
    if (!filtered.length) return message.info("Nothing to export");
    const stamp = new Date().toISOString().slice(0, 10);
    downloadText(`undr_results_${market.toLowerCase()}_${stamp}.csv`, toCSV(filtered, CSV_COLUMNS), "text/csv");
    return null;
  };

  const set = (k, v) => setFilters((f) => ({ ...f, [k]: v }));
  const toggleAction = (a) =>
    setFilters((f) => ({ ...f, actions: f.actions.includes(a) ? f.actions.filter((x) => x !== a) : [...f.actions, a] }));

  const sub = loaded
    ? rows.length
      ? `${rows.length} stocks scored · Jitta-style ranking · quality ≥ ${filters.minQuality} shown${scanDate ? ` · ${fmtDate(scanDate)}` : ""}`
      : "No results yet — run a scan to score the universe"
    : "Loading…";

  return (
    <>
      <PageHeader title="Results" sub={sub}>
        <Segmented options={MARKET_TABS} value={market} onChange={setMarket} label="Market" />
        <Button icon={<IconDownload />} onClick={exportCsv}>
          Export CSV
        </Button>
        <Button variant="primary" icon={<IconRefresh />} onClick={runScan} disabled={starting || scan.active}>
          {scan.active ? "Scanning…" : "Run scan now"}
        </Button>
      </PageHeader>

      <ScanProgress {...scan} />

      <div className="stat-grid">
        <Stat label="BUY signals" value={stats.buys} color="var(--good)" sub={stats.perMarket} />
        <Stat label="Below fair value" value={stats.below} suffix={`/ ${stats.withFair}`} sub="price ≤ 10-year payback" />
        <Stat label="Avg quality" value={isNum(stats.avgQ) ? stats.avgQ.toFixed(1) : "—"} suffix="/ 10" sub={`${stats.q5} stocks with quality ≥ 5`} />
        <Stat label="Portfolio changes" value={portfolioChanges ?? "—"} sub={nextRebalance} />
        <Stat
          label="Data quality"
          value={stats.flagTotal}
          color={stats.flagTotal ? "var(--warn)" : undefined}
          sub={stats.flagSub || "no data flags"}
        />
      </div>

      <div className="filters">
        <label className="search-box">
          <IconSearch />
          <span className="sr-only">Search ticker or company</span>
          <input
            type="search"
            placeholder="Ticker or company…"
            value={filters.search}
            onChange={(e) => set("search", e.target.value)}
          />
        </label>
        <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
          <span style={{ fontSize: 12, color: "var(--muted)" }}>Action</span>
          {["BUY", "WATCH", "AVOID"].map((a) => (
            <button
              key={a}
              type="button"
              className={`chip ${a.toLowerCase()}`}
              aria-pressed={filters.actions.includes(a)}
              onClick={() => toggleAction(a)}
            >
              {a}
            </button>
          ))}
        </div>
        <label>
          Min quality
          <input
            type="range"
            className="range"
            min={0}
            max={10}
            step={0.5}
            value={filters.minQuality}
            onChange={(e) => set("minQuality", Number(e.target.value))}
          />
          <span className="num" style={{ color: "var(--ink)", fontWeight: 500 }}>{filters.minQuality.toFixed(1)}</span>
        </label>
        <label>
          Sector
          <select className="select" value={filters.sector} onChange={(e) => set("sector", e.target.value)}>
            <option value="">All sectors</option>
            {sectors.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        </label>
        <label>
          Sort
          <select className="select" value={filters.sortBy} onChange={(e) => set("sortBy", e.target.value)}>
            {SORT_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
          <select
            className="select"
            aria-label="Sort order"
            value={filters.sortOrder}
            onChange={(e) => set("sortOrder", e.target.value)}
          >
            <option value="desc">↓ Desc</option>
            <option value="asc">↑ Asc</option>
          </select>
        </label>
        <span style={{ marginLeft: "auto", fontSize: 12, color: "var(--muted)" }}>
          Showing {filtered.length} of {stats.total}
        </span>
      </div>

      <div className="table-wrap">
        <div className="table-scroll">
          <table className="tbl">
            <thead>
              <tr>
                <th style={{ width: 44 }}>#</th>
                <th style={{ width: 200 }}>Company</th>
                <th style={{ width: 84 }}>Action</th>
                <th style={{ width: 150 }}>Quality</th>
                <th style={{ width: 90 }}>Rank</th>
                <th style={{ width: 100 }}>Price</th>
                <th style={{ width: 120 }}>Fair value</th>
                <th style={{ width: 90 }}>Upside</th>
                <th style={{ width: 90 }}>Loss chance</th>
                <th>Timing</th>
                <th style={{ width: 180 }}>Flags</th>
              </tr>
            </thead>
            <tbody>
              {pageRows.map((r) => (
                <Row key={`${r.market}-${r.ticker}`} r={r} colorRows={colorRows} />
              ))}
            </tbody>
          </table>
        </div>
        {loaded && pageRows.length === 0 && (
          <Empty title={rows.length ? "No stocks match these filters" : "No scan results"}>
            {rows.length ? "Try lowering the minimum quality or enabling more actions." : "Run a scan or start the backend to load results."}
          </Empty>
        )}
        <Pager
          page={cur}
          pages={pages}
          onChange={setPage}
          from={filtered.length ? (cur - 1) * PAGE_SIZE + 1 : 0}
          to={Math.min(cur * PAGE_SIZE, filtered.length)}
          total={filtered.length}
        />
      </div>
    </>
  );
}
